import { useState, useCallback } from "react";
import { View, Text, Pressable, Image, Linking } from "react-native";
import * as Clipboard from "expo-clipboard";
import {
  Package,
  Truck,
  Check,
  Copy,
  ChevronRight,
  ChevronDown,
  Phone,
  ClipboardCheck,
  FlaskConical,
  CircleCheckBig,
} from "lucide-react-native";
import { getMedicationByBrandName } from "@src/packages/content";
import type { ContentBlock } from "@src/packages/content";
import MedicationFactsheetSheet from "../../../components/MedicationFactsheetSheet";
import type { MedicationUserContext } from "../../../components/MedicationFactsheetSheet";
import ComingSoonOverlay from "../../../components/ComingSoonOverlay";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { ComingSoonSheet } from "../../../components/ComingSoonSheet";
import { EMBER } from "../../../../onboarding/theme";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  ENABLE_PHARMACY_MOCK_DATA,
  MOCK_ORDER,
  type DeliveryOrder,
  type DeliveryStep,
} from "../../access/pharmacyMockData";

const STEP_INDEX: Record<DeliveryStep, number> = {
  placed: 0,
  preparing: 1,
  shipped: 2,
  out_for_delivery: 3,
  delivered: 4,
};

const STEP_LABEL_KEYS = [
  "care.delivery.step_placed",
  "care.delivery.step_preparing",
  "care.delivery.step_shipped",
  "care.delivery.step_in_transit",
  "care.delivery.step_delivered",
];

function StepIconForIndex({
  idx,
  color,
  size,
  strokeWidth,
}: {
  idx: number;
  color: string;
  size: number;
  strokeWidth: number;
}) {
  switch (idx) {
    case 0:
      return (
        <ClipboardCheck size={size} color={color} strokeWidth={strokeWidth} />
      );
    case 1:
      return (
        <FlaskConical size={size} color={color} strokeWidth={strokeWidth} />
      );
    case 2:
      return <Package size={size} color={color} strokeWidth={strokeWidth} />;
    case 3:
      return <Truck size={size} color={color} strokeWidth={strokeWidth} />;
    case 4:
      return (
        <CircleCheckBig size={size} color={color} strokeWidth={strokeWidth} />
      );
    default:
      return null;
  }
}

const pillCapsuleImage =
  require("../../../../assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("../../../../assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("../../../../assets/images/injection-pen.png") as number;

function MedFormIcon({
  form,
  size = 28,
}: {
  form: "pen" | "vial" | "pill" | "suppository";
  size?: number;
}) {
  const src =
    form === "pen"
      ? injectionPenImage
      : form === "vial"
        ? vialSyringeImage
        : pillCapsuleImage;
  return (
    <Image
      source={src}
      style={{ width: size, height: size }}
      resizeMode="contain"
    />
  );
}

export default function DeliveryTrackingCard() {
  const t = useTranslations();
  const capturePress = usePressCapture("DeliveryTrackingCard");
  const order: DeliveryOrder | null = ENABLE_PHARMACY_MOCK_DATA
    ? MOCK_ORDER
    : null;
  const currentIdx = order ? STEP_INDEX[order.currentStep] : 0;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [factsheet, setFactsheet] = useState<ContentBlock<"medication"> | null>(
    null,
  );
  const [userCtx, setUserCtx] = useState<MedicationUserContext | undefined>(
    undefined,
  );
  const [comingSoonSheetOpen, setComingSoonSheetOpen] = useState(false);

  const handleMedPress = useCallback(
    (med: DeliveryOrder["medications"][number]) => {
      const sheet = getMedicationByBrandName(med.name) ?? null;
      if (!sheet) return;
      setFactsheet(sheet);
      setUserCtx({ dosage: med.dosage, route: med.route });
    },
    [],
  );

  if (!order) {
    return (
      <>
        <View style={{ marginTop: 16 }}>
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("care.delivery.title")),
                "care.delivery.title",
              );
              setComingSoonSheetOpen(true);
            }}
            style={{
              ...(GLASS_CARD_STYLE as object),
              padding: 18,
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}
          >
            <Package size={18} color={EMBER.colors.warm} strokeWidth={1.5} />
            <View style={{ flex: 1, gap: 3 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 15,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                }}
              >
                {t("care.delivery.title")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              >
                {t("care.delivery.not_connected_body")}
              </Text>
            </View>
            <ChevronRight
              size={16}
              color={EMBER.colors.textFaint}
              strokeWidth={1.5}
            />
          </Pressable>
        </View>

        <ComingSoonSheet
          open={comingSoonSheetOpen}
          onClose={() => setComingSoonSheetOpen(false)}
          title={t("care.delivery.not_connected_sheet_title")}
          description={t("care.delivery.not_connected_sheet_description")}
        />
      </>
    );
  }

  return (
    <>
      <View style={{ marginTop: 16 }}>
        <ComingSoonOverlay
          description="Track shipments from your pharmacy here once we partner with delivery providers."
          onPress={() => setComingSoonSheetOpen(true)}
        >
          <Pressable
            onPress={() => {
              capturePress("toggle_delivery_details");
              setExpanded((v) => !v);
            }}
            style={{
              ...(GLASS_CARD_STYLE as object),
              padding: 0,
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <View
              className="flex-row"
              style={{
                alignItems: "flex-start",
                gap: 10,
                paddingHorizontal: 18,
                paddingTop: 16,
                paddingBottom: 14,
              }}
            >
              <Package
                size={18}
                color={EMBER.colors.warm}
                strokeWidth={1.5}
                style={{ marginTop: 2 }}
              />
              <View style={{ flex: 1 }}>
                <View
                  className="flex-row"
                  style={{ alignItems: "center", gap: 8 }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 15,
                      fontWeight: "700",
                      color: EMBER.colors.text,
                    }}
                  >
                    {t("care.delivery.title")}
                  </Text>
                  {/*
                The inline StatusBadge previously sat here. The card is now
                wrapped in a ComingSoonOverlay (below) which renders a centred
                badge over the whole preview, so a second inline badge would
                duplicate the cue.
              */}
                </View>
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 12, color: EMBER.colors.textLight }}
                >
                  {order.pharmacyName} · {order.estimatedDelivery}
                </Text>
              </View>
              {expanded ? (
                <ChevronDown
                  size={16}
                  color={EMBER.colors.textFaint}
                  strokeWidth={1.5}
                />
              ) : (
                <ChevronRight
                  size={16}
                  color={EMBER.colors.textFaint}
                  strokeWidth={1.5}
                />
              )}
            </View>

            {/* Step indicators */}
            <View
              style={{
                paddingHorizontal: 28,
                paddingTop: 4,
                paddingBottom: expanded ? 16 : 20,
              }}
            >
              {/* Steps row — icon + label stacked, lines between */}
              <View
                className="flex-row"
                style={{
                  alignItems: "flex-start",
                }}
              >
                {order.steps.map((step, idx) => {
                  const isCompleted = idx < currentIdx;
                  const isCurrent = idx === currentIdx;
                  const isLast = idx === order.steps.length - 1;
                  const iconColor = isCompleted
                    ? EMBER.colors.textMid
                    : isCurrent
                      ? EMBER.colors.warm
                      : EMBER.colors.textFaint;

                  return (
                    <View
                      key={step.key}
                      className="flex-row"
                      style={{
                        flex: isLast ? 0 : 1,
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Icon + label column */}
                      <View style={{ alignItems: "center", width: 48 }}>
                        <View
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            backgroundColor: isCurrent
                              ? `${EMBER.colors.warm}14`
                              : isCompleted
                                ? `${EMBER.colors.textMid}10`
                                : `${EMBER.colors.textFaint}08`,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <StepIconForIndex
                            idx={idx}
                            size={15}
                            color={iconColor}
                            strokeWidth={1.8}
                          />
                        </View>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 10,
                            fontWeight: isCurrent ? "600" : "400",
                            color: isCurrent
                              ? EMBER.colors.warm
                              : isCompleted
                                ? EMBER.colors.textMid
                                : EMBER.colors.textFaint,
                            textAlign: "center",
                            marginTop: 6,
                          }}
                        >
                          {t(STEP_LABEL_KEYS[idx]!)}
                        </Text>
                      </View>

                      {/* Connecting line — vertically centered with the icon */}
                      {!isLast && (
                        <View
                          style={{
                            flex: 1,
                            height: 1.5,
                            backgroundColor: isCompleted
                              ? EMBER.colors.textLight
                              : EMBER.colors.border,
                            marginTop: 15,
                            marginHorizontal: 2,
                          }}
                        />
                      )}
                    </View>
                  );
                })}
              </View>
            </View>

            {/* Expanded detail */}
            {expanded && (
              <View>
                {/* ETA + Tracking */}
                <View
                  className="flex-row"
                  style={{
                    marginHorizontal: 18,
                    marginBottom: 14,
                    paddingHorizontal: 14,
                    paddingVertical: 14,
                    borderRadius: 10,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <Truck
                    size={18}
                    color={EMBER.colors.textMid}
                    strokeWidth={1.5}
                  />
                  <View style={{ flex: 1, gap: 3 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        fontWeight: "500",
                        color: EMBER.colors.text,
                      }}
                    >
                      {t("care.delivery.estimated_arrival", {
                        params: { time: order.estimatedTime },
                      })}
                    </Text>
                    <Pressable
                      onPress={async () => {
                        capturePress(
                          pressLabel(
                            copied
                              ? t("care.delivery.copied")
                              : t("care.delivery.tracking_label", {
                                  params: { number: order.trackingNumber },
                                }),
                          ),
                          "copy_tracking_number",
                        );
                        await Clipboard.setStringAsync(order.trackingNumber);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="flex-row"
                      style={{ alignItems: "center", gap: 5 }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 12, color: EMBER.colors.textLight }}
                      >
                        {copied
                          ? t("care.delivery.copied")
                          : t("care.delivery.tracking_label", {
                              params: { number: order.trackingNumber },
                            })}
                      </Text>
                      {copied ? (
                        <Check
                          size={11}
                          color={EMBER.colors.green}
                          strokeWidth={2}
                        />
                      ) : (
                        <Copy
                          size={11}
                          color={EMBER.colors.textFaint}
                          strokeWidth={1.5}
                        />
                      )}
                    </Pressable>
                  </View>
                </View>

                {/* Medication rows */}
                <View style={{ paddingBottom: 4 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      fontWeight: "600",
                      color: EMBER.colors.textFaint,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      paddingHorizontal: 18,
                      paddingBottom: 4,
                    }}
                  >
                    {t("care.delivery.in_this_shipment")}
                  </Text>
                  {order.medications.map((med) => (
                    <Pressable
                      key={med.name}
                      onPress={() => {
                        capturePress(
                          pressLabel(med.name),
                          "delivery_medication_row",
                        );
                        handleMedPress(med);
                      }}
                      className="flex-row"
                      style={{
                        alignItems: "center",
                        paddingHorizontal: 18,
                        paddingVertical: 10,
                        gap: 12,
                      }}
                    >
                      <MedFormIcon form={med.form} />
                      <View style={{ flex: 1 }}>
                        <View
                          className="flex-row"
                          style={{
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 14,
                              fontWeight: "500",
                              color: EMBER.colors.text,
                            }}
                          >
                            {med.name}
                          </Text>
                          <View
                            style={{
                              backgroundColor: `${EMBER.colors.textMid}15`,
                              paddingHorizontal: 8,
                              paddingVertical: 2,
                              borderRadius: 6,
                            }}
                          >
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 11,
                                fontWeight: "700",
                                color: EMBER.colors.textMid,
                              }}
                            >
                              {med.dosage}
                            </Text>
                          </View>
                        </View>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 12,
                            color: EMBER.colors.textLight,
                            marginTop: 2,
                          }}
                        >
                          {med.route}
                        </Text>
                      </View>
                      <ChevronRight
                        size={16}
                        color={EMBER.colors.textFaint}
                        strokeWidth={1.5}
                      />
                    </Pressable>
                  ))}
                </View>

                {/* Contact pharmacy */}
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(
                        t("care.delivery.contact_pharmacy", {
                          params: { pharmacy: order.pharmacyName },
                        }),
                      ),
                      "contact_pharmacy",
                    );
                    Linking.openURL(`tel:${order.pharmacyPhone}`);
                  }}
                  className="flex-row"
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    marginHorizontal: 18,
                    marginBottom: 16,
                    marginTop: 4,
                    paddingVertical: 12,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: EMBER.colors.border,
                  }}
                >
                  <Phone
                    size={14}
                    color={EMBER.colors.textMid}
                    strokeWidth={1.5}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      fontWeight: "500",
                      color: EMBER.colors.textMid,
                    }}
                  >
                    {t("care.delivery.contact_pharmacy", {
                      params: { pharmacy: order.pharmacyName },
                    })}
                  </Text>
                </Pressable>
              </View>
            )}
          </Pressable>
        </ComingSoonOverlay>
      </View>

      <MedicationFactsheetSheet
        item={factsheet}
        onClose={() => {
          setFactsheet(null);
          setUserCtx(undefined);
        }}
        userContext={userCtx}
      />

      <ComingSoonSheet
        open={comingSoonSheetOpen}
        onClose={() => setComingSoonSheetOpen(false)}
        title={t("care.delivery.not_connected_sheet_title")}
        description={t("care.delivery.not_connected_sheet_description")}
      />
    </>
  );
}
